import articleApi from '@/api/ArticleApi';
import { getProductById } from '@/api/PaymentApi';
import { getAnalysisTypeIdFromProduct } from '@/utils/analysisRouteMap';

/** 분석권 상품이 등록된 게시판 ID */
export const TICKET_BOARD_ID = 2521;

/** 사용자 구매 대상이 아닌 상품 글 제외 (개발/테스트/관리자 지급용) */
const isTestArticle = (title) => /\[개발\]|\[테스트\]|지급용/.test(title || '');

/**
 * 실제 구매 가능한 상품인지 (판매가능 + 활성).
 * 업셀 카드는 상품 상세로 직접 이동하므로, 목록 노출(isPublic) 여부와 무관하게 구매 가능하면 노출한다.
 * 판매중지(isAvailable=false)·비활성(status≠ACTIVE) 상품은 클릭 시 구매 불가하므로 제외.
 */
const isOnSale = (product) =>
  product?.isAvailable === true && product?.status === 'ACTIVE';

/**
 * 문자열에서 회차/단위를 추출. "5건"→{5,건}, "2회"→{2,회}, "1개월"→{1,개월}.
 * "10/20회"처럼 가격이 어느 회차에 대응하는지 모호한 표기는 단가 산정에서 제외(null).
 */
const AMBIGUOUS_COUNT = /\d+\s*\/\s*\d+\s*(?:회|건|개월)/;
const parseUnit = (text) => {
  if (!text || AMBIGUOUS_COUNT.test(text)) return null;
  const m = String(text).match(/(\d+)\s*(회|건|개월)/);
  return m ? { count: Number(m[1]), unit: m[2] } : null;
};

/** 옵션의 회차/단위: 옵션명 → 옵션 detail.count → 게시글 제목 순으로 시도 */
const resolveUnit = (option, articleTitle) => {
  const byName = parseUnit(option?.name);
  if (byName) return byName;
  const detailCount = (option?.detail || []).reduce(
    (mx, d) => Math.max(mx, Number(d?.count) || 0),
    0,
  );
  if (detailCount > 0) return { count: detailCount, unit: '회' };
  return parseUnit(articleTitle);
};

/** 상품의 인원수(낭만궁합 1:1=2, 1:2=3, 1:3=4). detail[0].memberCount 기준 */
const getMemberCount = (product) => product?.detail?.[0]?.memberCount ?? null;

/**
 * 상품 옵션들 중 1건당 단가가 가장 낮은 옵션의 표시 정보를 계산.
 * (1건당 단가 = (product.pointPrice + 옵션 추가가) / 옵션 회차)
 * 회차를 알 수 없는 옵션은 제외하며, 산정 가능한 옵션이 없으면 null.
 * @param {object} product - 상품 객체 (게시판 article.product 또는 /payments/products 단건 응답)
 * @param {string} [articleTitle] - 회차 추출 fallback용 제목 (게시글 제목 또는 상품명)
 * @param {{dayRange?:number|null, monthRange?:number|null, modeKey?:string|null}} [optionFilter]
 *   - 택일 등 기간별(dayRange/monthRange) 또는 낭만작명 등 모드별(modeKey) 옵션 한정
 * @returns {{ perUnitPrice:number, unit:string, discountPct:number } | null}
 */
const pickBestOption = (
  product,
  articleTitle,
  { dayRange = null, monthRange = null, modeKey = null } = {},
) => {
  const base = Number(product.pointPrice);
  const baseOrigin = Number(product.basePointPrice ?? base);
  const options = (product.optionGroups || []).flatMap((g) => g.options || []);
  // 옵션이 없는 상품은 상품 자체를 단일 옵션으로 간주
  let optionList = options.length
    ? options
    : [{ pointPrice: 0, basePointPrice: 0, detail: product.detail }];

  // 옵션이 회차가 아니라 모드 선택지인 상품(낭만작명 등)은 현재 모드 옵션만 단가 대상으로 한정.
  // 일치 옵션이 없으면(회차형 상품 등) 전체 옵션 대상 유지.
  if (modeKey) {
    const modeMatched = optionList.filter((o) =>
      (o.detail || []).some((d) => d?.modeKey === modeKey),
    );
    if (modeMatched.length) optionList = modeMatched;
  }

  // 옵션에서 회차를 못 얻는 상품(모드 옵션 + 상품 자체가 1회권인 구조)은 상품 detail의 회차로 fallback
  const productCount = (product.detail || []).reduce(
    (mx, d) => Math.max(mx, Number(d?.count) || 0),
    0,
  );
  const productUnit = productCount > 0 ? { count: productCount, unit: '회' } : null;

  let best = null;
  for (const option of optionList) {
    // 택일 등 기간별 옵션 필터 (옵션 detail의 dayRange/monthRange 일치만)
    if (dayRange != null || monthRange != null) {
      const od = (option.detail || [])[0] || {};
      if (dayRange != null && od.dayRange !== dayRange) continue;
      if (monthRange != null && od.monthRange !== monthRange) continue;
    }

    const resolved = resolveUnit(option, articleTitle) ?? productUnit;
    if (!resolved?.count) continue;

    const total = base + Number(option.pointPrice || 0);
    const totalOrigin = baseOrigin + Number(option.basePointPrice || 0);
    const perUnitPrice = Math.round(total / resolved.count);
    const discountPct =
      totalOrigin > total && totalOrigin > 0
        ? Math.round(((totalOrigin - total) / totalOrigin) * 100)
        : 0;

    if (!best || perUnitPrice < best.perUnitPrice) {
      best = { perUnitPrice, unit: resolved.unit, discountPct };
    }
  }
  return best;
};

/**
 * 분석권 상품(게시판 article) 조회 유틸.
 *
 * 결제 팝업에서 "할인 분석권" 업셀(최저 1건당 단가 + 할인율)을 보여주고,
 * 해당 분석권 상품 상세로 이동하기 위해 사용한다.
 * 연결 상품은 서버가 분석 타입/옵션에 지정한 recommendedProductId를 최우선으로 쓴다 —
 * /payments/products/{productId} 단건 조회로 articleId를 얻어 연결하므로 게시판 미노출 상품도 연결된다.
 * 지정이 없거나 조회 실패·판매 불가면 기존 방식(게시판에서 product.detail[].analysisTypeId 매칭
 * 상품 중 1건당 단가가 가장 낮은 옵션)으로 fallback 한다.
 *
 * 1건당 단가 = (product.pointPrice + 옵션 추가가) / 옵션 회차
 * 회차를 안전하게 알 수 없는 옵션(빈 옵션명·"X/Y회권" 등)은 산정에서 제외한다.
 */
export const useTicketProduct = () => {
  /**
   * analysisTypeId에 해당하는 할인 분석권 상품 정보를 반환.
   * recommendedProductId가 주어지고 해당 상품이 구매 가능하면 그 상품을, 아니면 최저 단가 상품을 고른다.
   * @param {number} analysisTypeId
   * @param {object} [options]
   * @param {string|null} [options.recommendedProductId] - 서버가 분석 타입/옵션별로 지정한 추천 상품 ID
   *   (예: "p_26060800001"). 단건 조회로 articleId를 얻어 단가 비교 없이 해당 상품으로 연결한다.
   * @param {string|null} [options.modeKey] - 낭만작명처럼 한 상품의 옵션이 모드 선택지인 경우,
   *   현재 모드와 일치하는 옵션만 단가 산정 대상으로 한정한다. (예: "recommend-person-kor")
   * @param {number|null} [options.memberCount] - 낭만궁합처럼 인원수별 상품이 갈리는 경우,
   *   현재 인원수(detail[0].memberCount)와 일치하는 상품만 후보로 한정한다.
   * @param {number|null} [options.dayRange] - 택일처럼 기간별 옵션이 갈리는 경우,
   *   옵션 detail의 dayRange와 일치하는 옵션만 단가 산정 대상으로 한정한다. (예: 7일권 → 7)
   * @param {number|null} [options.monthRange] - 위와 동일, 월 단위 기간. (예: 30일권 → 1)
   * @returns {Promise<{ boardId:number, articleId:number, perUnitPrice:number|null, unit:string|null, discountPct:number } | null>}
   */
  const fetchTicketProductByTypeId = async (
    analysisTypeId,
    {
      recommendedProductId = null,
      modeKey = null,
      memberCount = null,
      dayRange = null,
      monthRange = null,
    } = {},
  ) => {
    if (!analysisTypeId && !recommendedProductId) return null;

    // 1순위: 서버 지정 추천 상품 — productId 단건 조회로 articleId를 얻어 바로 연결.
    // 게시판 목록에 노출되지 않는(미노출) 상품도 연결되며, 판매 불가 상품만 기존 방식으로 fallback.
    if (recommendedProductId) {
      try {
        const product = await getProductById(recommendedProductId);
        if (product?.articleId && isOnSale(product)) {
          const best = pickBestOption(product, product.name, { dayRange, monthRange, modeKey });
          return {
            boardId: TICKET_BOARD_ID,
            articleId: product.articleId,
            // 회차 표기가 없어 단가 산정이 안 되면 카드엔 "상품 보기"로 노출 (이동은 그대로 동작)
            perUnitPrice: best?.perUnitPrice ?? null,
            unit: best?.unit ?? null,
            discountPct: best?.discountPct ?? 0,
          };
        }
      } catch (e) {
        // 단건 조회 실패(미등록 productId 등) → 기존 방식 fallback
      }
    }

    try {
      const res = await articleApi().list(TICKET_BOARD_ID, {
        page: 1,
        size: 100,
        productBundleType: 'SINGLE',
        sort: ['displayDate.desc'],
        systemId: import.meta.env.VITE_APP_SYSTEMID,
      });

      const candidates = (res?.content || []).filter(
        (item) =>
          getAnalysisTypeIdFromProduct(item.product) === Number(analysisTypeId) &&
          item.product?.pointPrice != null &&
          isOnSale(item.product) &&
          !isTestArticle(item.articleTtl) &&
          (memberCount == null || getMemberCount(item.product) === Number(memberCount)),
      );

      let best = null; // { perUnitPrice, unit, discountPct, articleId }
      for (const item of candidates) {
        const optionBest = pickBestOption(item.product, item.articleTtl, {
          dayRange,
          monthRange,
          modeKey,
        });
        if (optionBest && (!best || optionBest.perUnitPrice < best.perUnitPrice)) {
          best = { ...optionBest, articleId: item.articleId };
        }
      }
      if (!best) return null;

      return {
        boardId: TICKET_BOARD_ID,
        articleId: best.articleId,
        perUnitPrice: best.perUnitPrice,
        unit: best.unit,
        discountPct: best.discountPct,
      };
    } catch (e) {
      console.error('[useTicketProduct] 분석권 상품 조회 실패:', e);
      return null;
    }
  };

  return { fetchTicketProductByTypeId };
};
